/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useCallback, useEffect, useState } from 'react';

import InputField from 'components/LoginForm/Input';

import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

import {
  clearCampaignRegistrationCode,
  getCampaignRegistrationCode,
} from 'helpers/campaignRegistrationCode';
import { performLogin } from 'helpers/performLogin';

import { useRegistrationSchema } from 'hooks/useRegistrationSchema';

import { agencyAffiliations, businessDescriptions, countries, hostAgencies, states } from './data';
import type { CheckboxGroupOptions, SchemaField, SchemaSection } from './types';
import {
  buildApiPayload,
  evaluateCondition,
  getClearedUpdatesForNonVisibleFields,
  resolveConditionalValue,
  TEXT_INPUT_TYPES,
  validateSchemaFields,
} from './utils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import i18n from 'i18n-react';
import { HashLink as Link } from 'react-router-hash-link';
import { cssTransition, toast } from 'react-toastify';
import SweetAlert from 'sweetalert-react';

const Zoom = cssTransition({
  enter: 'zoomIn',
  exit: 'zoomOut',
  duration: [500, 800],
});

const resolverMap: Record<string, { value: string; label: string }[]> = {
  countries,
  states,
  agencyAffiliations,
  hostAgencies,
  businessDescriptions,
};

const QUICK_REGISTER_EMAIL_FIELD = 'reg_email';
const QUICK_REGISTER_HIDDEN_FIELDS = ['reg_password', 'reg_passwordConfirm'];

interface RegisterProps {
  userInput: {
    registration_code: string;
    reg_first_name: string;
    reg_last_name: string;
    reg_email: string;
    reg_agency_name: string;
    reg_password: string;
    reg_passwordConfirm: string;
    email: string;
    [key: string]: unknown;
  };
  change: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  /** Programmatic field updates (e.g. schema defaults); avoids synthetic events for static analysis. */
  updateFormField: (name: string, value: string | boolean) => void;
  back: () => void;
  loadingAnimRegister: boolean;
  checkInputValidity: () => boolean;
  checkInputValidityFields: () => string[];
  setAnim: (type: string, value: boolean) => void;
  enableMarketing: () => void;
  /** When false, password fields are hidden and email is disabled (quick-register; user already logged in). */
  passwordField?: boolean;
}

export const Register = ({
  userInput,
  change,
  updateFormField,
  back,
  loadingAnimRegister,
  checkInputValidity,
  checkInputValidityFields,
  setAnim,
  enableMarketing,
  passwordField = true,
}: RegisterProps) => {
  const { schema, isLoading } = useRegistrationSchema();
  const isQuickRegister = passwordField === false;

  // Internal state for submission
  const [appId, setAppId] = useState<string | number>(0);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [popUpMarketing, setPopUpMarketing] = useState(false);

  const handleCheckboxOptionChange = useCallback(
    (itemName: string, itemValue: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      change({
        target: { name: itemName, value: e.target.checked ? itemValue : '' },
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [change],
  );

  // Load app info on mount
  useEffect(() => {
    DataHandler.matchAppId().then((id: string | number) => setAppId(id));
    StorageHandler.load(storageKeys.appInfo).then(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (appInfo: any) => {
        if (appInfo) {
          if (appInfo.clientId) setClientId(appInfo.clientId as string);
          if (appInfo.clientSecret) setClientSecret(appInfo.clientSecret as string);
        }
      },
    );
  }, []);

  useEffect(() => {
    const [userTypeField] = schema?.userTypeSelector?.fields ?? [];
    const current = userInput.registration_code.trim();

    if (userTypeField && current === '') {
      const defaultItem = userTypeField.options.items.find((item) => item.default);
      const value = defaultItem?.value ?? '';

      updateFormField('registration_code', value);
    }
  }, [schema, userInput.registration_code, updateFormField]);

  // Sync all other schema defaults (defaultValue, defaultFrom, default option) into parent state when empty
  useEffect(() => {
    if (schema) {
      const userTypeField = schema.userTypeSelector.fields[0];
      const defaultUserTypeItem = userTypeField.options.items.find((item) => item.default);
      const effectiveSelectedUserType = (userInput.registration_code.trim() || defaultUserTypeItem?.value) ?? '';
      const activeSchemaForDefaults = schema.schemas[effectiveSelectedUserType];

      if (activeSchemaForDefaults) {
        const fields: SchemaField[] = [];
        for (const section of activeSchemaForDefaults.sections) {
          for (const sub of section.subsections) {
            for (const field of sub.fields) fields.push(field);
          }
        }
        const hasRegCountryUS = fields.some((f) => f.name === 'reg_country' && f.defaultValue === 'US');
        for (const field of fields) {
          if (field.type === 'checkboxGroup' && field.options?.source === 'inline') {
            const groupOptions = field.options as CheckboxGroupOptions;
            for (const item of groupOptions.items) {
              if (userInput[item.name] === undefined) {
                updateFormField(item.name, false);
              }
            }
            continue;
          }

          const current = userInput[field.name];
          const isEmpty =
            current === undefined ||
            current === '' ||
            (field.type === 'checkbox' && current !== true && current !== 'on' && current !== 'true');
          if (!isEmpty) continue;

          if (field.defaultValue !== undefined) {
            const val = field.defaultValue;
            if (field.type === 'checkbox') {
              if (
                hasRegCountryUS &&
                (field.name === 'marketing_opt_in' || field.name === 'opt_in_marketing') &&
                val === false
              ) {
                continue;
              }
              updateFormField(field.name, Boolean(val));
            } else {
              const strVal =
                typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean' ? String(val) : '';
              if (strVal !== '') {
                updateFormField(field.name, strVal);
              }
            }
            continue;
          }
          if (field.defaultFrom) {
            const fromVal = userInput[field.defaultFrom];
            // Skip booleans: login form can store `false` for empty text (value || checked), which must not become "false".
            if (typeof fromVal === 'string' && fromVal.trim() !== '') {
              updateFormField(field.name, fromVal);
            } else if (typeof fromVal === 'number' && !Number.isNaN(fromVal)) {
              updateFormField(field.name, String(fromVal));
            }
            continue;
          }
          if (field.options?.source === 'inline' && field.options.items.length > 0) {
            const defaultItem = field.options.items.find((item) => item.default);
            if (defaultItem?.value) {
              updateFormField(field.name, defaultItem.value);
            }
          }
        }
      }
    }
  }, [schema, userInput, updateFormField]);

  // Clear stored values for fields that are currently hidden by `visible` (unique `name` only — skips duplicate names e.g. reg_state).
  useEffect(() => {
    if (!schema) return;
    const userTypeField = schema.userTypeSelector.fields[0];
    const defaultUserType = userTypeField.options.items.find((item) => item.default);
    const selected = (userInput.registration_code || defaultUserType?.value) ?? '';
    const active = schema.schemas[selected];
    if (!active) return;

    const cleared = getClearedUpdatesForNonVisibleFields(active, userInput);
    for (const [key, value] of Object.entries(cleared)) {
      updateFormField(key, value);
    }
  }, [schema, userInput, updateFormField]);

  if (isLoading || !schema) {
    return (
      <div className="Register register-loading">
        <FontAwesomeIcon icon={['fas', 'circle-notch']} spin size="2x" />
      </div>
    );
  }

  const { userTypeSelector } = schema;
  const userTypeField = userTypeSelector.fields[0];
  const defaultUserType = userTypeField.options.items.find((item) => item.default);
  const selectedUserType = (userInput.registration_code || defaultUserType?.value) ?? '';
  const activeSchema = schema.schemas[selectedUserType];

  /**
   * Register handler — builds the API object from the schema and submits.
   * Called after SSO registration succeeds, or directly for quick-register.
   */
  const registerHandler = () => {
    if (!activeSchema) return;

    if (checkInputValidity()) {
      setAnim('loadingAnimRegister', true);

      const registerObject = buildApiPayload(userInput, activeSchema.submit, userTypeSelector);

      void getCampaignRegistrationCode().then((campaignCode) => {
        if (campaignCode) {
          registerObject.registration_code = campaignCode;
        }

        DataHandler.matchAppId().then((resolvedAppId: string | number) => {
          void APIHandler.setUser(appId, registerObject).then(async () => {
            const realEmail = (registerObject.email ?? userInput.email) as string;
            void APIHandler.getUserOld(resolvedAppId, realEmail).then((newUser: unknown) => newUser);
            APIHandler.registerUserProgress(realEmail);
            if (campaignCode) {
              clearCampaignRegistrationCode();
            }

            const copy = i18n.translate('loginScreen.version2.success') as string;

            toast.info(`${i18n.translate(copy) as string}`, {
              position: 'top-center',
              transition: Zoom,
              autoClose: 10000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            setAnim('loadingAnimRegister', false);

            let shouldRedirectToMain = false;
            const password = userInput.reg_password;
            if (realEmail && password && typeof password === 'string' && password.trim() !== '') {
              try {
                const result = await performLogin(realEmail, password, clientId, clientSecret);
                if (!result.success) {
                  toast.error(result.message ?? 'Login failed. Please try signing in on the login page.', {
                    position: 'top-center',
                    transition: Zoom,
                    autoClose: 10000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                  });
                } else if (!result.redirectToQuickRegister) {
                  shouldRedirectToMain = true;
                }
              } catch {
                toast.info('Please log in with your new account.', {
                  position: 'top-center',
                  transition: Zoom,
                  autoClose: 5000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                });
              }
            }

            window.setTimeout(() => {
              if (window.location.href.includes('mobile')) {
                window.ReactNativeWebView.postMessage('back', '*');
              } else {
                window.location.href = shouldRedirectToMain ? '/main' : '/login';
              }
            }, 2000);
          });
        });
      });
    } else {
      toast.error(
        `${
          i18n.translate('loginScreen.version2.errors.fillInAllFields') as string
        } ${checkInputValidityFields().join(', ')}`,
        {
          position: 'top-center',
          transition: Zoom,
          autoClose: 10000,
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
      setAnim('loadingAnimRegister', false);
    }
  };

  /**
   * SSO registration handler — validates fields, registers SSO user,
   * then calls registerHandler for platform registration.
   */
  const registerHandlerSSO = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Marketing opt-in popup check
    if (
      userInput.opt_in_marketing !== 'on' &&
      userInput.opt_in_marketing !== true &&
      !localStorage.getItem('newsletter')
    ) {
      setPopUpMarketing(true);
      localStorage.setItem('newsletter', 'true');
      event.preventDefault();
      setAnim('loadingAnimRegister', false);
      return;
    }

    const submitButton = event.target as HTMLButtonElement;
    submitButton.disabled = true;
    event.preventDefault();
    setAnim('loadingAnimRegister', true);

    // TODO: These SSO fields are hardcoded. Consider adding an ssoMapping to the schema's submit config
    // to make these dynamic (e.g. { name: ["reg_first_name", "reg_last_name"], email: "reg_email", ... })
    const firstName = userInput.reg_first_name;
    const lastName = userInput.reg_last_name;
    const email = userInput.reg_email || userInput.email;
    const company = userInput.reg_agency_name;
    const password = userInput.reg_password;
    const passwordConfirm = userInput.reg_passwordConfirm;

    // For quick-register, skip SSO and go directly to register
    if (window.location.href.includes('quick-register')) {
      registerHandler();
      submitButton.disabled = false;
      return;
    }

    // Validate fields per schema rules
    if (activeSchema) {
      const validationErrors = validateSchemaFields(activeSchema, userInput);
      if (validationErrors.length > 0) {
        toast.error(validationErrors[0], {
          position: 'top-center',
          transition: Zoom,
          autoClose: 10000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        submitButton.disabled = false;
        setAnim('loadingAnimRegister', false);
        return;
      }
    }

    // Check required fields and register SSO user
    if (checkInputValidity()) {
      void APIHandler.registerSSOUser(
        `${firstName} ${lastName}`,
        email,
        company,
        password,
        passwordConfirm,
        clientId,
        clientSecret,
      ).then((SSOUser: { success: boolean }) => {
        if (SSOUser.success === false) {
          toast.error(i18n.translate('loginScreen.version2.errors.emailExists') as string, {
            position: 'top-center',
            transition: Zoom,
            autoClose: 10000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          submitButton.disabled = false;
          setAnim('loadingAnimRegister', false);
        } else {
          // SSO succeeded, register platform user
          registerHandler();
          submitButton.disabled = false;
        }
      });
    } else {
      toast.error(
        (i18n.translate('loginScreen.version2.errors.fillInAllFields') as string) +
          ' ' +
          checkInputValidityFields().join(', '),
        {
          position: 'top-center',
          transition: Zoom,
          autoClose: 10000,
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
      submitButton.disabled = false;
      setAnim('loadingAnimRegister', false);
    }
  };

  return (
    <div className="Register">
      <div
        className={clsx('register__section', {
          'register__section--hidden': userTypeSelector.hidden,
        })}
        aria-hidden={userTypeSelector.hidden ? true : undefined}
      >
        <div className="step mt-1">{i18n.translate(userTypeSelector.titleKey)}</div>

        <div className="radioButtons">
          {userTypeField.options.items.map((option) => (
            <label
              key={option.value}
              htmlFor={option.value}
              className={selectedUserType === option.value ? 'checked' : ''}
            >
              <input
                type="radio"
                value={option.value}
                id={option.value}
                name={userTypeField.name}
                onChange={change}
                required={userTypeField.required}
                checked={selectedUserType === option.value}
              />
              {i18n.translate(option.labelKey)}
            </label>
          ))}
        </div>
      </div>

      {activeSchema && (
        <>
          <p className="step-title header">{i18n.translate(activeSchema.titleKey)}</p>

          {activeSchema.sections.map((section: SchemaSection) => (
            <div
              key={section.id}
              className={clsx('register__section', 'register__section--stack', {
                'register__section--hidden': section.hidden,
              })}
              aria-hidden={section.hidden ? true : undefined}
            >
              <div className="step mt-1">{i18n.translate(section.titleKey)}</div>

              {section.subsections.map((subsection: SchemaSection['subsections'][number]) => (
                <div key={subsection.id}>
                  {/* TODO: Replace hardcoded 'privacy' check with a schema-driven variant/type property */}
                  {subsection.id === 'privacy' ? (
                    <div className="privacy">
                      <b>{i18n.translate(subsection.titleKey ?? '')}</b>
                      {subsection.descriptionKey && (
                        <>
                          <br />
                          {i18n.translate(subsection.descriptionKey)}
                        </>
                      )}
                    </div>
                  ) : subsection.titleKey ? (
                    <h5>
                      <b>{i18n.translate(subsection.titleKey)}</b>
                    </h5>
                  ) : null}

                  {subsection.fields.map((field: SchemaField) => {
                    if (!evaluateCondition(field.visible, userInput)) {
                      return null;
                    }
                    if (isQuickRegister && QUICK_REGISTER_HIDDEN_FIELDS.includes(field.name)) {
                      return null;
                    }

                    const isRequired =
                      typeof field.required === 'boolean'
                        ? field.required
                        : evaluateCondition(field.required, userInput);

                    const fieldValue = userInput[field.name];

                    if (TEXT_INPUT_TYPES.includes(field.type)) {
                      const placeholderKey = field.placeholderKey ?? resolveConditionalValue(field.labelKey, userInput);
                      const placeholder = i18n.translate(placeholderKey) as string;

                      const fallback = field.defaultFrom ? userInput[field.defaultFrom] : undefined;
                      const defaultVal =
                        typeof fieldValue === 'string' && fieldValue
                          ? fieldValue
                          : typeof fallback === 'string'
                            ? fallback
                            : '';

                      return (
                        <InputField
                          key={field.name}
                          change={change}
                          fieldType={field.type}
                          fieldName={field.name}
                          fieldPlaceholder={placeholder}
                          icon={field.icon}
                          line
                          defaultVal={defaultVal}
                          required={isRequired}
                          disabled={isQuickRegister && field.name === QUICK_REGISTER_EMAIL_FIELD}
                        />
                      );
                    }

                    if (field.type === 'checkbox') {
                      const labelKey = resolveConditionalValue(field.labelKey, userInput);
                      const label = i18n.translate(labelKey) as string;

                      const isChecked = fieldValue === true || fieldValue === 'on' || fieldValue === 'true';

                      return (
                        <span key={field.name} className="checkItem">
                          {/* key includes isChecked to force remount — InputField uses defaultChecked (uncontrolled) */}
                          <InputField
                            key={`${field.name}-${String(isChecked)}`}
                            change={change}
                            fieldLabel={label}
                            fieldName={field.name}
                            fieldType="checkbox"
                            line={false}
                            title={label}
                            checked={isChecked}
                            required={isRequired}
                            userSelect={false}
                          />
                        </span>
                      );
                    }

                    if (field.type === 'select' && field.options) {
                      const labelKey = resolveConditionalValue(field.labelKey, userInput);
                      const label = i18n.translate(labelKey) as string;

                      let options: { value: string; label: string }[] = [];
                      if (field.options.source === 'resolver') {
                        options = resolverMap[field.options.resolver] ?? [];
                      } else if (field.options.source === 'inline') {
                        options = field.options.items.map((item) => ({
                          value: item.value,
                          label: i18n.translate(item.labelKey) as string,
                        }));
                      }

                      const defaultValue = typeof field.defaultValue === 'string' ? field.defaultValue : '';
                      const currentValue = typeof fieldValue === 'string' ? fieldValue : defaultValue;

                      return (
                        <React.Fragment key={field.name}>
                          <p className={field.name}>
                            {label}
                            {isRequired ? ' *' : ''}
                          </p>
                          <select id={field.name} name={field.name} onChange={change} value={currentValue}>
                            <option value="" disabled>
                              {i18n.translate('loginScreen.version2.fields.select') as string}
                            </option>
                            {options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </React.Fragment>
                      );
                    }

                    if (field.type === 'checkboxGroup' && field.options?.source === 'inline') {
                      const groupOptions = field.options as CheckboxGroupOptions;
                      const labelKey = resolveConditionalValue(field.labelKey, userInput);
                      const label = i18n.translate(labelKey) as string;
                      const columns = groupOptions.columns ?? 1;

                      return (
                        <React.Fragment key={field.name}>
                          <p className={field.name}>
                            {label}
                            {isRequired ? ' *' : ''}
                          </p>
                          <div
                            className="segments-group"
                            style={{ gridTemplateColumns: `repeat(${columns}, auto)` }}
                          >
                            {groupOptions.items.map((item) => {
                              const optionLabel = i18n.translate(item.labelKey) as string;
                              const isOptionChecked = Boolean(userInput[item.name]);

                              return (
                                <span key={item.name} className="checkItem">
                                  {/* key includes isOptionChecked to force remount — InputField uses defaultChecked (uncontrolled) */}
                                  <InputField
                                    key={`${item.name}-${String(isOptionChecked)}`}
                                    change={handleCheckboxOptionChange(item.name, item.value)}
                                    fieldLabel={optionLabel}
                                    fieldName={item.name}
                                    fieldType="checkbox"
                                    line={false}
                                    title={optionLabel}
                                    checked={isOptionChecked}
                                    required={false}
                                    userSelect={false}
                                  />
                                </span>
                              );
                            })}
                          </div>
                        </React.Fragment>
                      );
                    }

                    return null;
                  })}
                </div>
              ))}
            </div>
          ))}

          <div className="login__form--buttons">
            {window.location.pathname !== '/register-mobile' &&
              window.location.pathname !== '/login/register-mobile' &&
              window.location.pathname !== '/login/quick-register-mobile' && (
                <Link to="/login">
                  <button type="button" onClick={back} className="Email__input--button btnInverse">
                    {i18n.translate('general.back')}
                  </button>
                </Link>
              )}
            <button type="submit" onClick={registerHandlerSSO} className="Email__input--button">
              {loadingAnimRegister ? (
                <span>
                  <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
                </span>
              ) : (
                i18n.translate('loginScreen.register')
              )}
            </button>
          </div>
        </>
      )}

      <SweetAlert
        title=""
        show={popUpMarketing}
        onConfirm={() => {
          enableMarketing();
          setPopUpMarketing(false);
        }}
        text={i18n.translate('loginScreen.version2.marketingOptIn')}
        onCancel={() => {
          setPopUpMarketing(false);
        }}
        confirmButtonText={i18n.translate('loginScreen.version2.optIn')}
        cancelButtonText={i18n.translate('loginScreen.version2.optOut')}
        showCancelButton
      />
    </div>
  );
};
